import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { FcGoogle } from "react-icons/fc";
import { IoClose } from "react-icons/io5";
import { useAuth } from "../contexts/AuthContext";
import { GlassPanel, Input, Button, SPRING_SMOOTH, TAP, cx } from "./ds";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { AuthLockup } from "../pages/Auth/AuthLayout";
import "../pages/Auth/auth.css";

// DS-restyle only — all logic below (escape-to-close, submit, Google SSO
// redirect, loading/message state) is unchanged. Reuses the same visual
// language as the /login page (src/pages/Auth/Login.jsx + AuthLayout) via
// the shared auth.css classes, minus the full-page ambient glow (this is a
// modal over existing app content, not its own page).
//
// Responsive: >=768 keeps the centered modal card. <768 the card docks to
// the bottom as a frosted sheet (same grammar as AuthLayout's phone sheet)
// — it MAY keep its existing dismiss behaviors (Escape, the close button).
const LoginOverlay = ({ isOpen, onClose, onSwitchToSignup }) => {
  const { isPhone } = useBreakpoint();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Get the return path (where to redirect after login)
  const from = location.state?.from?.pathname || "/Overview";

  // Close on escape key
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape" && !isLoggingIn) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose, isLoggingIn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setMessage("Logging in...");

    try {
      await login(email, password);
      setMessage("Login successful!");

      // Successful login handling
      navigate(from, { replace: true }); // Navigate first
      // Then close the overlay
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err) {
      setMessage(err.message || "Login failed");
      setIsLoggingIn(false);
    }
  };

  const handleGoogle = () => {
    window.location.href = `${process.env.REACT_APP_API_URL}/auth/google`;
  };

  if (!isOpen) return null;

  const isGoodNews =
    message.includes("successful") || message.includes("Logging");

  return (
    <div
      className={cx(
        "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex",
        isPhone ? "items-end justify-center" : "items-center justify-center"
      )}
    >
      <motion.div
        initial={isPhone ? { y: "100%" } : { opacity: 0, y: 16, scale: 0.98 }}
        animate={isPhone ? { y: 0 } : { opacity: 1, y: 0, scale: 1 }}
        transition={SPRING_SMOOTH}
        style={
          isPhone
            ? { width: "100%", position: "relative" }
            : { width: "100%", maxWidth: 400, margin: 16, position: "relative" }
        }
      >
        <GlassPanel className={cx("ds-auth-card", isPhone && "ds-auth-sheet")}>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-icon"
            style={{ position: "absolute", top: 12, right: 12 }}
            disabled={isLoggingIn}
            aria-label="Close"
          >
            <IoClose size={20} />
          </button>

          <AuthLockup className="ds-auth-lockup--card" />
          <p className="ds-auth-eyebrow">ALGORITHMIC TRADING PLATFORM</p>

          <Button
            variant="secondary"
            size="lg"
            onClick={handleGoogle}
            disabled={isLoggingIn}
            style={{ width: "100%" }}
            as={motion.button}
            whileTap={TAP}
          >
            <FcGoogle size={18} />
            Sign in with Google
          </Button>

          <div className="ds-auth-divider">
            <span>or</span>
          </div>

          <form onSubmit={handleSubmit} className="ds-auth-form">
            <Input
              type="email"
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoggingIn}
              placeholder="you@example.com"
              required
            />

            <Input
              type="password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoggingIn}
              placeholder="••••••••"
              required
            />

            <div className="ds-auth-row">
              <button
                type="button"
                className="ds-auth-link"
                onClick={() => navigate("/forgot-password")}
                disabled={isLoggingIn}
              >
                Forgot password?
              </button>
            </div>

            <Button
              type="submit"
              variant="accent"
              size="lg"
              disabled={isLoggingIn}
              style={{ width: "100%" }}
              as={motion.button}
              whileTap={TAP}
            >
              {isLoggingIn ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          {message && (
            <p className={`ds-auth-message ${isGoodNews ? "is-success" : "is-error"}`}>
              {message}
            </p>
          )}

          <p className="ds-auth-links">
            Don't have an account?{" "}
            <button
              onClick={onSwitchToSignup}
              className="ds-auth-link"
              disabled={isLoggingIn}
            >
              Sign up
            </button>
          </p>
        </GlassPanel>
      </motion.div>
    </div>
  );
};

export default LoginOverlay;
